import React, { useState, useCallback, useMemo } from 'react';
import { Copy, Edit2, Save, X, RotateCcw, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from 'lucide-react';

interface CombinedSoapNoteEditorProps {
  /** The original SOAP note text from the LLM (already combined) */
  originalNote: string;
  /** The current edited combined note (if any edits have been saved) */
  editedCombinedNote?: string;
  /** Whether the section is expanded */
  isExpanded: boolean;
  /** Toggle section expansion */
  onToggleExpand: () => void;
  /** Callback when edits are saved */
  onSave?: (editedCombinedNote: string) => void;
  /** Whether saving is in progress */
  isSaving?: boolean;
  /** Callback for copy action */
  onCopy?: (text: string) => void;
  /** Whether copy was successful (for showing "Copied!" state) */
  isCopied?: boolean;
  /** Callback for feedback */
  onFeedback?: (isPositive: boolean) => void;
  /** Current feedback state */
  feedbackState?: boolean | null;
  /** Whether editing is disabled */
  disabled?: boolean;
}

export const CombinedSoapNoteEditor: React.FC<CombinedSoapNoteEditorProps> = ({
  originalNote,
  editedCombinedNote,
  isExpanded,
  onToggleExpand,
  onSave,
  isSaving = false,
  onCopy,
  isCopied = false,
  onFeedback,
  feedbackState,
  disabled = false,
}) => {
  // The original text is now a string directly from the LLM
  const originalCombinedText = originalNote || '';
  
  // The note to display (edited version takes precedence)
  const displayText = editedCombinedNote ?? originalCombinedText;
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editingText, setEditingText] = useState(displayText);
  
  // Track if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing) return false;
    return editingText !== displayText;
  }, [isEditing, editingText, displayText]);
  
  // Track if there are any edits from original
  const hasEditsFromOriginal = useMemo(() => {
    if (!editedCombinedNote) return false;
    return editedCombinedNote !== originalCombinedText;
  }, [editedCombinedNote, originalCombinedText]);
  
  const handleStartEdit = useCallback(() => {
    setEditingText(displayText);
    setIsEditing(true);
  }, [displayText]);
  
  const handleCancelEdit = useCallback(() => {
    setEditingText(displayText);
    setIsEditing(false);
  }, [displayText]);
  
  const handleResetToOriginal = useCallback(() => {
    setEditingText(originalCombinedText);
  }, [originalCombinedText]);
  
  const handleSave = useCallback(() => {
    if (!onSave) return;
    onSave(editingText);
    setIsEditing(false);
  }, [onSave, editingText]);
  
  const handleCopy = useCallback(() => {
    if (onCopy) {
      onCopy(displayText);
    }
  }, [onCopy, displayText]);
  
  return (
    <div className="bg-base-100 border border-base-300 rounded-lg">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleExpand}
            className="p-1 hover:bg-base-200 rounded transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          <span className="text-lg font-medium">SOAP Note</span>
          {hasEditsFromOriginal && !isEditing && (
            <span className="badge badge-info badge-sm">Edited</span>
          )}
          {hasUnsavedChanges && (
            <span className="badge badge-warning badge-sm">Unsaved changes</span>
          )}
        </div>
        
        <div className="flex items-center gap-2 flex-wrap self-end sm:self-auto">
          {/* Edit/Save buttons */}
          {!isEditing ? (
            <button
              className="btn btn-sm btn-outline gap-1"
              onClick={handleStartEdit}
              disabled={disabled || isSaving}
              title="Edit SOAP note"
            >
              <Edit2 className="w-3 h-3" />
              Edit
            </button>
          ) : (
            <>
              <button
                className="btn btn-sm btn-ghost gap-1"
                onClick={handleCancelEdit}
                disabled={isSaving}
                title="Cancel editing"
              >
                <X className="w-3 h-3" />
                Cancel
              </button>
              {hasUnsavedChanges && (
                <button
                  className="btn btn-sm btn-ghost gap-1"
                  onClick={handleResetToOriginal}
                  disabled={isSaving}
                  title="Reset to original LLM output"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset
                </button>
              )}
              <button
                className="btn btn-sm btn-primary gap-1"
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                title="Save changes"
              >
                <Save className="w-3 h-3" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
          
          {/* Copy/Feedback actions (only show when not editing) */}
          {!isEditing && (
            <div className="flex items-center space-x-1">
              <button
                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={handleCopy}
                title="Copy to clipboard"
              >
                <Copy className="w-3 h-3 mr-1" />
                {isCopied ? 'Copied!' : 'Copy'}
              </button>
              {onFeedback && (
                <>
                  <button
                    className={`inline-flex items-center p-1 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300 ${
                      feedbackState === true 
                        ? 'text-green-600 bg-green-50' 
                        : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                    }`}
                    onClick={() => onFeedback(true)}
                    title="Good"
                  >
                    <ThumbsUp className="w-3 h-3" />
                  </button>
                  <button
                    className={`inline-flex items-center p-1 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300 ${
                      feedbackState === false 
                        ? 'text-red-600 bg-red-50' 
                        : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                    }`}
                    onClick={() => onFeedback(false)}
                    title="Needs improvement"
                  >
                    <ThumbsDown className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Content */}
      {isExpanded && (
        <div className="p-4">
          {isEditing ? (
            <textarea
              className="textarea textarea-bordered w-full min-h-96 text-gray-800 leading-relaxed font-mono text-sm"
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              disabled={isSaving}
              placeholder="Enter SOAP note..."
            />
          ) : (
            <div className="bg-base-200 rounded-lg p-6">
              <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                {displayText || <span className="text-gray-400 italic">No SOAP note available</span>}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CombinedSoapNoteEditor;
